/**
 * 시작하기 · 문제 해결 · 용어 풀이 — 메뉴 하나에 매이지 않은 장.
 */
import { BookText, Rocket, Wrench } from 'lucide-react';
import type { ManualChapter } from '../types.js';

export const GETTING_STARTED: ManualChapter = {
  slug: 'getting-started',
  title: '처음 시작하기',
  summary: '로그인 방법과 콘솔 화면 구성, 콘텐츠가 화면에 나오기까지의 전체 순서를 안내합니다.',
  icon: Rocket,
  sections: [
    {
      id: 'about',
      title: 'SignageOne 은 무엇을 하나요',
      blocks: [
        {
          kind: 'text',
          text: '매장·로비·공장의 화면에 **무엇을, 언제, 어느 화면에** 재생할지 한 곳에서 정하고, 실제로 재생했는지 기록으로 남기는 서비스입니다. 세 부분으로 이루어져 있습니다.',
        },
        {
          kind: 'table',
          header: ['구성', '하는 일'],
          rows: [
            ['관리 콘솔', '지금 보고 있는 이 웹 화면. 담당자가 콘텐츠를 올리고 편성을 정합니다.'],
            ['서버', '정한 내용을 보관하고, 각 화면에 지금 재생할 편성을 알려 줍니다.'],
            ['에이전트', '화면에 연결된 PC·TV 박스에 설치하는 프로그램. 편성을 내려받아 재생하고, 인터넷이 끊겨도 재생을 이어 갑니다.'],
          ],
        },
      ],
    },
    {
      id: 'login',
      title: '로그인',
      blocks: [
        {
          kind: 'table',
          header: ['이런 화면이 보이면', '할 일'],
          rows: [
            ['로그인', '아이디(이메일)·비밀번호를 넣고 [[로그인]].'],
            ['사내 계정으로 로그인', '회사가 사내 계정(Entra ID·Okta 등)을 연결해 둔 경우입니다. 그 단추를 누르면 회사 로그인 화면을 거쳐 콘솔로 돌아옵니다. 처음 들어오면 계정이 저절로 만들어지고, 역할은 사내 계정 시스템에 정해 둔 대로입니다. 아래 “공급사 계정으로 로그인” 은 공급사 점검용입니다.'],
            ['비밀번호 변경 필요', '관리자가 만들어 준 임시 비밀번호로 처음 로그인한 경우입니다. 새 비밀번호(8자 이상)를 정하고 [[비밀번호 변경]].'],
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '비밀번호를 10번 틀리면 15분 동안 잠깁니다',
          text: '비밀번호를 직접 찾는 기능은 없습니다. 잊었거나 잠겼다면 관리자에게 **비밀번호 재설정**을 요청하세요. 새 임시 비밀번호를 받게 됩니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '8시간 동안 사용하지 않으면 로그인이 해제됩니다',
          text: '해제되면 콘솔이 자동으로 로그인 화면으로 돌아갑니다. 다른 기기에서 모두 로그아웃했거나 계정이 비활성화·재설정되었을 때도 같습니다. 다시 로그인하세요. 사내 계정으로 들어왔으면 12시간마다 사내 계정 시스템에 다시 확인합니다 — 퇴사 등으로 막힌 계정은 그때 끊깁니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '사내 계정 로그인이 안 될 때',
          text: '로그인 화면 위에 까닭이 빨갛게 나옵니다. “이 서비스를 쓸 권한이 없는 사내 계정입니다” 면 사내 계정 시스템에서 이 서비스 권한(그룹)을 받아야 합니다 — 사내 계정 담당 부서에 요청하세요. 그 밖의 까닭은 관리자에게 알리세요.',
        },
      ],
    },
    {
      id: 'layout',
      title: '콘솔 둘러보기',
      blocks: [
        { kind: 'figure', figure: 'console-layout', caption: '왼쪽 메뉴 · 위쪽 막대 · 가운데 작업 화면' },
        {
          kind: 'table',
          header: ['번호', '자리', '쓰임'],
          rows: [
            ['1', '왼쪽 메뉴', '매일 쓰는 메뉴가 MAIN · 운영 · 관리로 묶여 있습니다. 역할에 따라 보이는 메뉴가 다릅니다.'],
            ['2', '메뉴 맨 아래', '**사용 설명서**(이 문서).'],
            ['3', '검색', '콘텐츠·화면 이름을 2글자 이상 넣으면 바로 찾습니다. 키보드 / 키를 누르면 곧장 검색칸으로 이동합니다.'],
            ['4', '종 모양 알림', '긴급 오버라이드 작동, 오류를 알린 화면, 꺼진 화면이 있으면 빨간 숫자가 붙습니다. 누르면 해당 메뉴로 이동합니다.'],
            ['5', '이름 동그라미', '[[내 계정]] (비밀번호 변경)과 [[로그아웃]]. 담당 범위가 정해진 계정은 이름 아래에 “담당 · 강남 사옥” 처럼 맡은 범위가 적힙니다.'],
          ],
        },
        { kind: 'text', text: '아래는 **어느 목록·창에서나 같습니다.**' },
        {
          kind: 'table',
          header: ['무엇', '어떻게'],
          rows: [
            ['줄의 이름', '목록에서 **이름을 누르면 그 줄을 고치는 창**이 열립니다 — 플레이리스트·스케줄·캠페인은 편집 창, 화면 그룹·사업장·위치는 그 줄의 고치기, 사용자는 이름 바꾸기, 통합 관제는 서버 고치기입니다. 콘텐츠·화면은 이름을 누르면 상세 화면으로 가고 거기서 고칩니다. 고칠 권한이 없는 줄의 이름은 글자로만 보입니다(눌러도 아무 일이 없습니다).'],
            ['표 머리', '**열 이름을 누르면** 그 열로 정렬하고, 한 번 더 누르면 반대로 정렬합니다. 열 이름 옆의 필터 아이콘을 누르면 그 열의 값과 개수가 나오고, 체크한 값만 남습니다. 필터를 걸면 표 위에 “필터 결과 N · 전체 N” 과 [[필터 해제]] 가 붙습니다. 정렬은 이 브라우저에 기억됩니다.'],
            ['쪽 나누기', '콘텐츠·화면·플레이리스트·스케줄·캠페인·리포트·사용자 목록은 24줄을 넘으면 아래에 **쪽 번호**가 붙습니다. 오른쪽 칸에서 한 쪽에 볼 수(24·48·96)를 고르면 이 브라우저에 기억됩니다. 정렬·필터는 쪽이 아니라 **목록 전체**에 적용되고, 바꾸면 첫 쪽으로 돌아갑니다. 보던 쪽은 주소에 실려 상세를 열었다 돌아와도 그 쪽입니다.'],
            ['여러 쪽에서 고르기', '표 머리의 체크칸은 **지금 쪽만** 고릅니다. 고른 것은 쪽을 넘겨도 남고, 선택 막대에 “다른 쪽 3대 포함” 처럼 보이지 않는 쪽에 고른 수가 붙습니다. 한 쪽을 모두 고르면 막대에 “조건에 맞는 N대 모두 선택” 이 나와 조건에 맞는 전부를 한 번에 고릅니다. 검색·필터로 목록에서 빠진 것은 선택에서도 빠집니다.'],
            ['편성 고르기', '플레이리스트·스케줄·콘텐츠를 고르는 칸(스케줄의 평소 편성·규칙, 화면의 편성 배정, 그룹 편성, 캠페인·긴급의 플레이리스트)은 **어디서나 같은 모양**입니다. 닫힌 칸에도 고른 것의 종류 아이콘·이름·정보(항목 수·규칙 수·형식과 길이)가 보이고, 누르면 이름 검색칸과 종류 칸(전체·플레이리스트·스케줄·콘텐츠)이 열립니다. ↑·↓ 로 옮기고 Enter 로 고르며, Esc 는 목록만 닫습니다.'],
            ['검색·필터 유지', '검색어와 필터는 주소에 함께 실립니다 — 상세를 열었다가 [[목록으로]] 돌아와도, 새로 고쳐도 그대로이고 보던 자리로 돌아옵니다. 그 주소를 다른 사람에게 보내면 같은 조건의 목록이 열립니다.'],
            ['창 닫기', '창은 **Esc** 로 닫힙니다. 편성·캠페인·스케줄·템플릿·콘텐츠 정보를 고치던 중이면 “저장하지 않고 닫을까요?” 를 먼저 묻습니다 — 처음 초점이 [[계속 편집]] 이라 Enter 를 눌러도 고친 내용이 사라지지 않습니다.'],
            ['삭제 확인', '삭제처럼 되돌릴 수 없는 일은 대상 이름을 굵게 보여 주며 한 번 묻습니다. 처음 초점이 [[취소]] 입니다.'],
            ['완료 알림', '저장·삭제·배정이 끝나면 화면 아래에 “캠페인을 중지했습니다” 같은 한 줄이 잠시 떴다 사라집니다. 실패는 그 자리에 빨간 줄로 남습니다.'],
            ['시각', '“3분 전” 같은 시각에 마우스를 올리면 정확한 시각(2026-10-07 17:03:09)이 보입니다.'],
            ['키보드', '화면·콘텐츠 목록은 Tab 으로 줄에 가서 Enter 를 누르면 상세가 열립니다. 브라우저 탭에는 지금 화면(상세는 대상 이름까지)이 제목으로 붙어, 탭을 여러 개 열어도 구분됩니다.'],
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '휴대폰·태블릿에서 볼 때',
          text: '폭이 좁으면 왼쪽 메뉴가 숨고, 왼쪽 위 메뉴 단추(☰)를 누르면 서랍처럼 열립니다. PC에서는 메뉴 맨 아래 화살표로 메뉴를 아이콘만 남기고 접을 수 있습니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '설명서와 소개서를 파일로 받을 수 있습니다',
          text: '[[사용 설명서]] 첫 화면 위쪽에서 두 가지 문서를 내려받습니다. **사용 설명서**는 이 설명서 전체를 화면 그림과 번호까지 그대로 담은 문서이고, **시스템 소개서**는 이 시스템이 무엇을 하는지 콘솔 화면과 함께 한 장에 한 가지씩 보여 주는 슬라이드입니다. 각각 [[PDF]] 와 [[PPT]] 로 받습니다 — 내용은 같고, PDF 는 읽고 인쇄하는 판, PPT 는 회의실에서 넘겨 보는 판입니다. 시스템 소개서는 [[화면으로 보기]] 를 누르면 파일을 받지 않고 콘솔에서 바로 내려 읽을 수 있습니다. PPT 는 쪽을 그림으로 담아 어느 PC 에서 열어도 글꼴이 바뀌지 않습니다(대신 슬라이드에서 글자를 고칠 수는 없습니다). 콘솔을 열 수 없는 교육장이나 현장 점검, 인수인계 자리에서 활용하세요.',
        },
      ],
    },
    {
      id: 'first-run',
      title: '처음 한 번, 이 순서로',
      blocks: [
        { kind: 'figure', figure: 'quick-start', caption: '콘텐츠가 화면에 나오기까지 — 다섯 단계' },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '화면 기기에 **에이전트가 설치**되어 있는지 확인합니다.', body: '설치는 공급사가 합니다. 설치가 끝난 기기를 켜면 화면에 6자리 코드가 뜹니다. → “화면 등록하기” 장' },
            { title: '기기에 뜬 6자리 코드로 **화면을 등록**합니다.', body: '[[화면]] 메뉴 → [[화면 추가]]. → “화면 등록하기” 장' },
            { title: '재생할 **콘텐츠를 올립니다.**', body: '[[콘텐츠]] 메뉴에서 이미지·영상을 올리거나 템플릿으로 만듭니다. → “콘텐츠” 장' },
            { title: '콘텐츠를 **플레이리스트**로 묶습니다.', body: '[[플레이리스트]] 메뉴에서 순서와 노출 시간을 정합니다. → “플레이리스트” 장' },
            { title: '화면에 플레이리스트(또는 스케줄)를 **배정**합니다.', body: '화면 상세의 “편성 배정” 에서 고르면 약 30초 뒤 화면에 반영됩니다. 화면 상세 위쪽의 지금 화면에서 실제 모습을 확인하세요.' },
          ],
        },
      ],
    },
  ],
};

export const TROUBLESHOOTING: ManualChapter = {
  slug: 'troubleshooting',
  title: '문제 해결',
  summary: '“화면이 나오지 않는다”, “바꿨는데 그대로다” 처럼 자주 겪는 상황과 확인 순서를 모았습니다.',
  icon: Wrench,
  sections: [
    {
      id: 'screen',
      title: '화면이 이상할 때',
      blocks: [
        {
          kind: 'faq',
          items: [
            {
              q: '콘솔에서 화면이 “오프라인” 으로 보입니다.',
              a: '① 기기와 모니터 전원이 켜져 있는지, ② 기기의 인터넷(와이파이·랜선)이 연결되어 있는지 확인하세요. 기기는 오프라인이어도 받아 둔 콘텐츠를 계속 재생할 수 있으니 현장 화면도 함께 확인하세요. 전원을 켠 뒤 초록 점으로 바뀌기까지 1~2분 걸립니다.',
            },
            {
              q: '오프라인 화면에서 [바로 보내기] 와 다운로드된 콘텐츠 지우기가 눌리지 않습니다.',
              a: '둘 다 기기가 **지금** 수행해야 하는 작업이라, 꺼진 화면에는 보내도 전달되지 않습니다 — 그래서 막아 두었습니다. 다시 연결되면(초록 점) 사용할 수 있습니다. 편성 배정·정비 PIN·업데이트 채널처럼 미리 정해 두는 설정은 오프라인이어도 바꿀 수 있고, 기기가 다시 연결될 때 받아 갑니다. 여러 화면을 골라 바로 보내면 켜진 화면에만 전달하고 꺼진 화면은 제외합니다.',
            },
            {
              q: '기기에 6자리 코드가 떠 있습니다.',
              a: '콘솔에 등록되지 않은 상태입니다. 처음 설치했거나, 콘솔에서 화면을 삭제했거나, 정비 모드에서 서버 주소를 바꾼 경우입니다. [[화면]] → [[화면 추가]] 에서 코드를 넣어 다시 등록하세요.',
            },
            {
              q: '기기에 “편성이 배정되지 않았습니다” 가 뜹니다.',
              a: '화면 상세의 “편성 배정” 에서 플레이리스트나 스케줄을 고르세요.',
            },
            {
              q: '기기가 “정비 모드” 화면에 멈춰 있습니다.',
              a: '현장에서 정비 모드를 열고 닫지 않은 것입니다. 기기 화면 오른쪽 위의 **×** 를 누르세요. 원격으로는 닫을 수 없습니다.',
            },
            {
              q: '화면이 완전히 검게 나옵니다.',
              a: '먼저 기기 전원을 껐다 켜세요. 특정 영상이 나올 차례에만 검게 나온다면 기기가 그 영상 형식을 재생하지 못하는 것이니, 해상도·화질을 낮춘 일반 MP4 로 다시 만들어 올려 보세요. 오래된 TV 박스에서 자주 발생합니다.',
            },
            {
              q: '한 콘텐츠만 깨지거나 나오지 않습니다.',
              a: '내려받다 만 파일일 수 있습니다. 화면 상세 “다운로드된 콘텐츠” 에서 그 파일을 골라 지우면 기기가 새로 내려받습니다.',
            },
          ],
        },
      ],
    },
    {
      id: 'schedule',
      title: '콘텐츠·편성이 생각대로 나오지 않을 때',
      blocks: [
        {
          kind: 'faq',
          items: [
            {
              q: '편성을 바꿨는데 화면이 그대로입니다.',
              a: '① 기기는 약 30초마다 확인하니 잠시 기다리세요. ② 큰 영상이면 기기에 “콘텐츠 적용 중 NN%” 가 보일 수 있습니다. ③ 그래도 그대로면 화면 상세의 “편성 결정 근거” 를 확인하세요. 캠페인·바로 보내기·긴급 오버라이드가 위에 걸려 있으면 스케줄보다 먼저 나옵니다.',
            },
            {
              q: '파일이 올라가지 않습니다.',
              a: '이미지는 JPEG·PNG·GIF·WebP, 영상은 MP4·WebM 만 받습니다. 인터넷 주소로 접속했다면 100MB 가 넘는 파일은 올라가지 않으니 용량을 줄여 주세요.',
            },
            {
              q: '콘텐츠가 삭제되지 않습니다.',
              a: '플레이리스트에 들어 있는 콘텐츠는 지울 수 없습니다. 먼저 플레이리스트에서 [[제거]] 하고 저장하세요. 다시 쓸 가능성이 있다면 [[보관]] 이 안전합니다.',
            },
            {
              q: '영상이 노출 시간보다 길게 나옵니다.',
              a: '정상입니다. 영상은 노출 시간과 상관없이 끝까지 재생된 뒤 다음 콘텐츠로 넘어갑니다. 영상을 짧게 틀려면 영상 파일 자체를 잘라 다시 올리세요.',
            },
          ],
        },
      ],
    },
    {
      id: 'account',
      title: '로그인·권한',
      blocks: [
        {
          kind: 'faq',
          items: [
            {
              q: '“로그인 시도가 많아 잠겼습니다” 가 뜹니다.',
              a: '15분 뒤 다시 시도하거나, 관리자에게 비밀번호 재설정을 요청하세요. 재설정하면 잠김도 함께 풀립니다.',
            },
            {
              q: '다른 사람에게는 보이는 메뉴가 저에게는 보이지 않습니다.',
              a: '역할이 다르기 때문입니다. 리포트는 리포트를 볼 수 있는 역할에게, 사용자는 관리자에게 보입니다. 필요한 업무를 관리자에게 알리고 역할을 요청하세요.',
            },
            {
              q: '다른 사옥의 화면이 목록에 보이지 않습니다.',
              a: '계정에 **담당 범위**가 정해져 있으면 맡은 화면 그룹의 화면만 보입니다. 오른쪽 위 이름 아래에 맡은 범위가 적혀 있습니다. 범위를 넓히려면 관리자에게 요청하세요.',
            },
            {
              q: '플레이리스트를 저장하려니 “담당 범위 밖에도 나갑니다” 라며 막힙니다.',
              a: '그 플레이리스트가 맡은 범위 밖의 화면(예: 본사 로비)에도 나가고 있어서입니다. 고치면 그 화면도 바뀌기 때문에 막습니다. 내 화면용 플레이리스트를 새로 만들어 배정하거나, 전체를 맡은 관리자에게 요청하세요. 스케줄·콘텐츠도 같습니다.',
            },
            {
              q: '화면은 보이는데 만들기·저장 단추가 없습니다.',
              a: '변경 권한이 없는 역할입니다. 권한이 없는 단추와 카드는 **표시되지 않습니다** — 눌렀다가 거절당하는 대신 처음부터 보이지 않습니다. 각 장 맨 위에 필요한 권한이 적혀 있으니, 그 권한을 관리자에게 요청하세요.',
            },
          ],
        },
      ],
    },
  ],
};

export const GLOSSARY: ManualChapter = {
  slug: 'glossary',
  title: '용어 풀이',
  summary: '콘솔과 이 설명서에 나오는 용어를 쉬운 말로 풀어 설명합니다.',
  icon: BookText,
  sections: [
    {
      id: 'terms',
      title: '가나다순',
      blocks: [
        {
          kind: 'table',
          header: ['용어', '뜻'],
          rows: [
            ['게시 기간', '콘텐츠가 화면에 나가도 되는 기간. 끝나면 어느 편성에 들어 있어도 빠집니다'],
            ['긴급 오버라이드', '재난·장애 때 모든 화면을 즉시 한 편성으로 덮는 가장 강한 편성'],
            ['그룹', '[[화면 그룹]] 메뉴나 연동 시스템에서 묶어 둔 화면 모음(예: 강남 사옥). 화면에 따로 배정한 편성이 없으면 그룹의 편성을 따르고, 담당 범위와 캠페인 대상으로도 씁니다'],
            ['담당 범위', '계정이 맡은 화면 그룹. 정해지면 그 그룹의 화면만 보고 고칩니다. 비어 있으면 고객사 전체입니다'],
            ['노출 시간', '콘텐츠 하나를 화면에 보여주는 시간(초)'],
            ['바로 보내기', '편성을 건드리지 않고 고른 화면에 임시로 콘텐츠를 띄우는 기능'],
            ['변환', '올린 영상을 모든 화면이 트는 모양(H.264 MP4)으로 바꾸는 일. 서버가 저절로 하고, 원본은 그대로 둡니다'],
            ['버전 고정', '화면 한 대를 특정 에이전트 버전에 묶어 새 버전을 받지 않게 하는 설정'],
            ['보관', '콘텐츠를 지우지 않고 목록에서만 숨기는 것. 복원할 수 있습니다'],
            ['숨기기', '화면을 목록·모니터링에서만 숨기는 것. 재생은 계속됩니다'],
            ['스케줄', '요일·시간대마다 다른 플레이리스트를 재생하도록 정한 편성표'],
            ['사내 계정', '회사의 로그인 시스템(Entra ID·Okta 등) 계정. 연결해 두면 따로 비밀번호 없이 그 계정으로 콘솔에 들어옵니다'],
            ['안정', '대부분의 화면이 새 에이전트 버전을 받는 채널'],
            ['에이전트', '화면 기기에 설치하는 재생 프로그램(플레이어)'],
            ['오프라인', '화면이 서버와 연결되지 않은 상태. 받아 둔 콘텐츠는 계속 재생할 수 있습니다'],
            ['운영 시간', '화면이 편성을 트는 시간. 그 밖에는 화면이 검게 쉽니다(긴급·바로 보내기는 나갑니다)'],
            ['위치', '화면이 설치된 곳의 이름(예: 본사 1층 로비). 모니터링에서 같은 위치끼리 묶입니다'],
            ['재생 증빙', '화면이 실제로 재생했다고 남긴 기록. 리포트의 근거입니다'],
            ['정비 모드', '현장에서 기기 상태를 보고 업데이트·주소 변경을 하는 화면. 여는 동안 재생이 멈춥니다'],
            ['카나리', '새 에이전트 버전을 몇 대에서 먼저 받아 시험하는 채널'],
            ['캠페인', '기간·대상을 정해 평소 편성 위에 덮어 재생하거나 끼워 넣는 편성'],
            ['콘텐츠', '화면에 나가는 재료 — 이미지·영상·웹 페이지·템플릿'],
            ['태그', '화면에 붙은 이름표(예: 로비, 수도권). 캠페인 대상을 고를 때 씁니다'],
            ['템플릿', '정해진 디자인에 글자만 채워 만드는 공지·홍보 화면'],
            ['페어링 코드', '설치된 기기 화면에 뜨는 6자리 코드. 콘솔 화면 추가에 넣어 기기를 등록합니다'],
            ['편성', '어느 화면에 무엇을 언제 재생할지 정한 것 — 플레이리스트·스케줄·캠페인을 통틀어'],
            ['플레이리스트', '콘텐츠를 재생할 순서대로 묶은 목록. 끝나면 처음부터 반복합니다'],
          ],
        },
      ],
    },
  ],
};
